import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Table, Spinner, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BsCalendar } from 'react-icons/bs';

import {
  getOrderDeliveryTypes,
  generatePickupOrder,
  insertOrder,
  placeBulkOrder,
} from '../../../services/requests/orders/orders-requests';

// Hour-of-day options (0-23) mirroring the legacy ddlTimeRequested dropdown.
const hourOptions = [
  { id: '', text: '--:--' },
  ...Array.from({ length: 24 }, (_, h) => ({ id: h, text: `${h < 10 ? '0' : ''}${h}:00` })),
];

/**
 * Legacy Pickup Order modal (btnPickupOrder → GeneratePickupOrder). Collects the
 * pickup date/time and delivery type, then creates the Dewar child order on the
 * server and navigates to it.
 */
export const PickupOrderModal: React.FC<any> = ({ show, onHide, parentOrderId, orderTypeId, deliveryTypeId }) => {
  const navigate = useNavigate();
  const [date, setDate] = useState('');
  const [hour, setHour] = useState<string | number>('');
  const [deliveryType, setDeliveryType] = useState<string | number>(deliveryTypeId ?? '');
  const [deliveryTypes, setDeliveryTypes] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (show && orderTypeId != null) {
      getOrderDeliveryTypes(orderTypeId).then((types) => {
        if (active) setDeliveryTypes(types ?? []);
      });
      setDeliveryType(deliveryTypeId ?? '');
    }
    return () => {
      active = false;
    };
  }, [show, orderTypeId, deliveryTypeId]);

  const generate = async () => {
    if (!date || deliveryType === '' || deliveryType == null) {
      setError('Date Requested and Delivery Type are required.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const created = await generatePickupOrder({
        parentOrderId,
        dateRequested: date,
        dateRequestedHour: hour === '' ? null : Number(hour),
        deliveryTypeId: Number(deliveryType),
      });
      if (created?.id) {
        onHide();
        navigate(`/orders/edit/${created.id}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Generate Pickup Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <Form.Group className="mb-3">
          <Form.Label>Date Requested</Form.Label>
          <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Time Requested</Form.Label>
          <Form.Select value={hour} onChange={(e) => setHour(e.target.value)}>
            {hourOptions.map((o) => (
              <option key={String(o.id)} value={o.id}>{o.text}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Delivery Type</Form.Label>
          <Form.Select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>
            <option value="">-- Select One --</option>
            {deliveryTypes.map((d) => (
              <option key={d.id ?? d.ID} value={d.id ?? d.ID}>{d.name ?? d.Name}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={generate} disabled={busy}>
          Generate{busy && <Spinner animation="border" size="sm" className="ms-1" />}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Legacy Multi-Order (bulk) modal (btnBulkOrder → BulkInsert). Up to 10 rows of
 * (Date Requested + PO Number); each non-empty row creates a new order that
 * copies the current form's field values with the per-row date/PO override.
 * Gas carrier mileage (legacy OBC distance service) is recomputed server-side by
 * the insert path, so no client-side distance handling is required.
 */
const ROWS = 10;

type DatePart = 'day' | 'month' | 'year';

// Day/month/year order of the browser locale, so pasted dates read the way the user sees them.
const localeDateOrder: DatePart[] = (() => {
  try {
    const parts: any[] = (new Intl.DateTimeFormat() as any).formatToParts(new Date(2001, 11, 31));
    const order = parts.map((p) => p.type).filter((t) => t === 'day' || t === 'month' || t === 'year');
    return order.length === 3 ? order : ['month', 'day', 'year'];
  } catch {
    return ['month', 'day', 'year'];
  }
})();

export const multiOrderDatePlaceholder = localeDateOrder
  .map((p) => (p === 'year' ? 'yyyy' : p === 'month' ? 'mm' : 'dd'))
  .join('/');

const pad2 = (n: number) => `${n < 10 ? '0' : ''}${n}`;

/** Parses a typed/pasted date (locale order, or yyyy-mm-dd) into ISO yyyy-mm-dd; null when invalid. */
export const parseMultiOrderDate = (text: string, order: DatePart[] = localeDateOrder): string | null => {
  const parts = (text ?? '').trim().split(/[/.\-\s]+/).filter(Boolean);
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  const values: Record<DatePart, number> = { day: 0, month: 0, year: 0 };
  const effective: DatePart[] = parts[0].length === 4 ? ['year', 'month', 'day'] : order;
  effective.forEach((key, i) => { values[key] = Number(parts[i]); });
  if (values.year < 100) values.year += 2000;
  const date = new Date(values.year, values.month - 1, values.day);
  if (date.getFullYear() !== values.year || date.getMonth() !== values.month - 1 || date.getDate() !== values.day) return null;
  return `${values.year}-${pad2(values.month)}-${pad2(values.day)}`;
};

const formatMultiOrderDate = (iso: string) => {
  const [year, month, day] = iso.split('-');
  const values: Record<DatePart, string> = { year, month, day };
  return localeDateOrder.map((p) => values[p]).join('/');
};

/** Text date input (paste-friendly, unlike the native date input) with a calendar button. */
const PasteableDateInput: React.FC<any> = ({ value, onChange, onPaste }) => {
  const pickerRef = useRef<HTMLInputElement>(null);
  const iso = parseMultiOrderDate(value);

  const openPicker = () => {
    const picker: any = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else picker.click();
  };

  return (
    <InputGroup size="sm" style={{ position: 'relative' }}>
      <Form.Control
        type="text"
        value={value}
        placeholder={multiOrderDatePlaceholder}
        isInvalid={!!value && !iso}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        onBlur={() => { if (iso) onChange(formatMultiOrderDate(iso)); }}
      />
      <Button variant="outline-secondary" onClick={openPicker} title="Pick a date" tabIndex={-1}>
        <BsCalendar />
      </Button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={iso ?? ''}
        onChange={(e) => { if (e.target.value) onChange(formatMultiOrderDate(e.target.value)); }}
        style={{ position: 'absolute', right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    </InputGroup>
  );
};

export const buildLegacyMultiOrderPayload = (base: any, dateRequested: string, ponumber: string) => ({
  ...base,
  id: null,
  dateRequested,
  ponumber,

  // Legacy BulkInsert creates a fresh order rather than copying lifecycle,
  // fulfillment, assignment, and international-processing state.
  dateOrdered: null,
  dateFulfilled: null,
  dateFulfilledTime: null,
  driverArrivalDateTime: null,
  driverArrivalHour: null,
  otherNotes: '',
  logisticsComments: '',
  containerId: '',
  bookingId: 0,
  paperworkPending: false,
  paperworkReceivedDate: null,
  returningContainerId: '',
  parentOrder: null,
  itn: '',
  transportMode: '',
  airFreight: '',
  oceanFreight: '',
  inlandFreight: '',
  creditCardOverride: false,
  driverUserId: '',
  exWorksOverride: false,
  highPressureContainerReq: false,
  options: '',
  carrierDeliveryId: null,
  returnChassisId: '',
  returnLocation: '',
  returnLocationOther: '',
  estimatedReturnDate: null,
  driverId: null,
  carrierMiles: 0,

  // The legacy insert maps the visible contact fields to both pairs and does
  // not persist OrderedByFax from its bulk-order call.
  contactName: base.orderedBy ?? null,
  contactPhone: base.orderedByPhone ?? null,
  orderedByFax: null,
});

export const MultiOrderModal: React.FC<any> = ({ show, onHide, getValues }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => Array.from({ length: ROWS }, () => ({ date: '', po: '' })));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const setRow = (i: number, key: 'date' | 'po', value: string) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };

  // A multi-line paste (e.g. a column copied from Excel) fills the following rows;
  // tab-separated lines starting in the date column fill Date + PO together.
  const pasteRows = (start: number, key: 'date' | 'po') => (e: React.ClipboardEvent<HTMLInputElement>) => {
    const lines = e.clipboardData.getData('text').replace(/\r/g, '').split('\n');
    while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
    const hasTabs = key === 'date' && lines.some((l) => l.includes('\t'));
    if (lines.length <= 1 && !hasTabs) return;
    e.preventDefault();
    setRows((prev) => prev.map((r, idx) => {
      const line = lines[idx - start];
      if (idx < start || line === undefined) return r;
      if (!hasTabs) return { ...r, [key]: line.trim() };
      const [date, po] = line.split('\t');
      return { ...r, date: (date ?? '').trim(), po: (po ?? r.po).trim() };
    }));
  };

  const create = async () => {
    // Validate: each row must have both fields or neither (legacy rule).
    const active = rows.filter((r) => r.date || r.po);
    if (active.some((r) => !r.date || !r.po)) {
      setError('Each order row must have both a Date Requested and a PO Number.');
      return;
    }
    if (active.length === 0) {
      setError('Enter at least one Date Requested + PO Number.');
      return;
    }
    if (active.some((r) => !parseMultiOrderDate(r.date))) {
      setError(`Enter each Date Requested as ${multiOrderDatePlaceholder}.`);
      return;
    }
    setError('');
    setBusy(true);
    try {
      const base = getValues?.() ?? {};
      for (const r of active) {
        const payload = buildLegacyMultiOrderPayload(base, parseMultiOrderDate(r.date) as string, r.po);
        // eslint-disable-next-line no-await-in-loop
        const created = await insertOrder(payload);
        // Legacy bulk creates AND places each order ("Bulk Order Placed").
        // eslint-disable-next-line no-await-in-loop
        if (created?.id) await placeBulkOrder(created.id);
      }
      onHide();
      // Route to My Recently Changed Orders for quick access to the just-created orders.
      navigate('/orders#My%20Recently%20Changed%20Orders');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Multi-Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <p className="text-muted small mb-2">
          Each row creates a new order copying this order's details, with the entered Date Requested and PO Number.
        </p>
        <Table size="sm" bordered>
          <thead>
            <tr><th style={{ width: '50%' }}>Date Requested</th><th>PO Number</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <PasteableDateInput value={r.date} onChange={(v: string) => setRow(i, 'date', v)} onPaste={pasteRows(i, 'date')} />
                </td>
                <td>
                  <Form.Control type="text" size="sm" value={r.po} onChange={(e) => setRow(i, 'po', e.target.value)} onPaste={pasteRows(i, 'po')} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={create} disabled={busy}>
          Create Orders{busy && <Spinner animation="border" size="sm" className="ms-1" />}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
